/**
 * Whether a failure can heal, read off the status code Firebase puts on it.
 *
 * A callable's `HttpsError` and a Firestore error carry the same vocabulary —
 * the gRPC status set, which `FunctionsErrorCodeCore` is the SDK's transcript
 * of. Every member of that set is classified below, so an SDK that mints a new
 * code breaks this file's build rather than quietly answering `unknown`.
 *
 * The answer is what a surface is allowed to say. "Try again" is true of a
 * `transient` failure and of nothing else: a `permanent` one has already been
 * judged, so a retry can only be refused the same way, and inviting the
 * operator to retry sends them round a loop this module already knows is
 * hopeless. `unknown` asserts neither — it is what an unrecognised code
 * resolves to, and the register it licenses is "something went wrong", which
 * stays true whichever the failure turns out to be.
 *
 * Only a string `err.code` is read, bare or under a callable's `functions/`.
 * `firebaseStatusCode` is that one reading, and every caller that branches on a
 * code reads it there — `failurePermanence` and `isPermissionDenied` included —
 * so no two readers can disagree about one error. A failure wrapped inside
 * another error is unwrapped by its caller; a numeric gRPC or HTTP carrier is
 * `retryOnTransient`'s to read.
 */
import type { FunctionsErrorCodeCore } from 'firebase/functions';

/** What a retry can do about a failure. */
export type FailurePermanence = 'permanent' | 'transient' | 'unknown';

/**
 * `satisfies` is what makes the table total: `Record` demands every code the
 * SDK declares, and rejects one it does not.
 */
const PERMANENCE_BY_CODE = {
  // Judged. The server read this request and refused it, so the identical
  // request can only be refused again.
  'invalid-argument': 'permanent',
  'failed-precondition': 'permanent',
  'not-found': 'permanent',
  'already-exists': 'permanent',
  'out-of-range': 'permanent',
  'permission-denied': 'permanent',
  // The remedy is credentials, not repetition: the SDK has already refreshed
  // whatever token it holds before the call that came back refused.
  unauthenticated: 'permanent',
  // "not implemented or not supported/enabled" — a deploy gap, which no
  // amount of waiting deploys.
  unimplemented: 'permanent',
  // "unrecoverable data loss or corruption" — the SDK's own word for it.
  'data-loss': 'permanent',

  // Never judged. The request did not reach a verdict, so the same request
  // can reach one on the next attempt.
  unavailable: 'transient',
  'deadline-exceeded': 'transient',
  internal: 'transient',
  aborted: 'transient',
  'resource-exhausted': 'transient',

  // Neither, deliberately — listed so a reader sees they were weighed.
  // `ok` is not a failure and never rides a thrown error. `cancelled` says
  // the caller stopped the call, which is no verdict on whether it would
  // have succeeded. `unknown` is gRPC's own name for a status it cannot
  // place, and this module cannot place it either.
  ok: 'unknown',
  cancelled: 'unknown',
  unknown: 'unknown',
} satisfies Record<FunctionsErrorCodeCore, FailurePermanence>;

/**
 * The gRPC status code on `err`, or null when `err` carries none.
 *
 * Firestore puts that code on its errors bare and a callable puts it under
 * `functions/`, so those two spellings are the only ones read. Every other
 * namespace is another SDK's own vocabulary, and Storage spells three of its
 * codes with gRPC's words — `unknown`, `unauthenticated`, `invalid-argument` —
 * so `storage/unauthenticated` reads as null, never as gRPC's refused call.
 *
 * Typed to the SDK's own code union, so a caller comparing against a misspelt
 * code fails to compile instead of branching on a value no error carries.
 */
export function firebaseStatusCode(err: unknown): FunctionsErrorCodeCore | null {
  if (typeof err !== 'object' || err === null || !('code' in err)) return null;
  const { code } = err;
  if (typeof code !== 'string') return null;
  const bare = code.startsWith('functions/') ? code.slice('functions/'.length) : code;
  // The table's keys are exactly the union — `satisfies` admits no other — so a
  // key equal to `bare` is `bare` read as a member of it.
  return (
    Object.keys(PERMANENCE_BY_CODE).find((known): known is FunctionsErrorCodeCore => known === bare) ??
    null
  );
}

/**
 * Whether retrying `err` can ever change the answer.
 *
 * Anything this module does not recognise — another SDK's code, a bare
 * `Error`, a thrown string, a numeric code — is `unknown`. That default is
 * the one deliberate choice here: guessing `transient` would re-create the
 * "Try again" that sent an operator round a hopeless loop, and guessing
 * `permanent` would tell them to stop for a failure that was about to heal.
 */
export function failurePermanence(err: unknown): FailurePermanence {
  const code = firebaseStatusCode(err);
  return code === null ? 'unknown' : PERMANENCE_BY_CODE[code];
}

/**
 * True when `err` is a Firebase permission denial — Firestore's
 * `permission-denied`, or a callable's `functions/permission-denied`.
 *
 * The one permanent failure with copy of its own: the rules refused this
 * principal this act, so the surface says "no access" rather than the generic
 * "this cannot succeed", and never folds it into an empty result — a denial
 * routes to the error channel so the UI stops short of painting a refused read
 * as no data.
 */
export function isPermissionDenied(err: unknown): boolean {
  return firebaseStatusCode(err) === 'permission-denied';
}

/**
 * The codes a retry can genuinely outlast, derived from the one table above so
 * `retryOnTransient` — which reads them off numeric and differently-cased
 * carriers this module does not touch — cannot disagree with it about which
 * failures heal.
 */
export const TRANSIENT_CODES: ReadonlySet<string> = new Set(
  Object.entries(PERMANENCE_BY_CODE)
    .filter(([, permanence]) => permanence === 'transient')
    .map(([code]) => code),
);
